// arcSweepEngine.js — the loader's "generation" animation, ported from the real
// Wheel's D3 arcTween (see src/components/D3Wheels/createPie.js's updateArcs():
// startAngle stays fixed, endAngle is interpolated from startAngle up to the
// segment's own endAngle every frame, so a wedge visibly GROWS CLOCKWISE within
// its own angular span — see the comment above its `.attrTween("d", ...)`).
// BrandWheelLoader has no D3 selection to run a transition on, so this module
// reproduces the same math by hand: it shares brandWheel.js's `wedgePathD` (the
// same radii/dividers/rim as the canonical Wheel — geometry can never drift
// between the two) and regenerates a wedge's `d` at a growing end angle.
//
// The one thing createPie's mount entrance doesn't need that the loader does:
// STAGGERING each segment's sweep one after another instead of every wedge
// growing at once (ISP-9999 #908, Gordon 2026-08-21 — "segment 1 sweeps in,
// THEN segment 2 starts, THEN segment 3", never simultaneous, never an opacity
// fade). Pure functions only — no DOM, no timers — so the sequencing and the
// arc-growth math are each directly unit-testable without a real animation
// frame or a browser.
//
// DRAW-OUT / HANDOFF (ISP-9999 #908 regression, Gordon frame-measured on
// a9cd212a, 2026-08-21 — "the ring goes FULL -> BLANK in one 70ms frame, every
// cycle handoff"): the bug was NEVER the draw-IN cascade above (that stays
// exactly as-is) — it was that `cycleDurationMs` used to end the instant the
// hold finished, so the very next frame jumped straight to a freshly-swapped,
// unswept wheel (every segment progress 0 => every `d` empty) with nothing in
// between. The fix is a third phase, appended after the hold, that is the
// MIRROR of the draw-in: each segment's arc SHRINKS (endAngle sweeping back
// down to its own startAngle) sequentially — ONE AT A TIME, same as the
// draw-in (`RETRACT_STAGGER_MS` == `SEGMENT_RETRACT_MS`, so segment N+1 never
// starts leaving until segment N has fully gone; ISP-9999 #935 — a shorter,
// overlapping stagger was tried first and produced a 2-at-once CASCADE
// instead of a relay, so it was corrected to match the IN phase's own
// whole-window stagger) so the ring empties as one smooth relay instead of a
// snap-to-blank. `computeFrame` is the only caller of this timeline, so
// index.jsx's rAF loop (which just asks `computeFrame` for "what does every
// path's `d` look like right now" and "are we done yet") needed no changes at
// all to pick this up.
//
// DIRECTION (ISP-9999 #966, Gordon 2026-08-21 — "generate CW from midnight,
// degenerate CCW from midnight"): brandWheel.js's `a0 = 0` starting point is
// midnight (12 o'clock — see `wedgePathD`'s `pt` helper), and its wedge angles
// increase CLOCKWISE from there. GENERATE already walks segments in that same
// array order (segment 0 at midnight, then 1, 2, ... clockwise around the
// ring) with each wedge's own arc growing clockwise within its span — no
// change needed. DEGENERATE must unwind from that same midnight point but
// COUNTER-CLOCKWISE: the wedge whose fixed startAngle equals 0 (segment 0
// itself) must be the LAST to retract, and the wedge adjacent to midnight from
// the other side (the highest-index segment) must be the FIRST — see
// `retractOrderIndex` below, which is the only change this required (the
// per-segment shrink math already sweeps its own visible edge back toward its
// fixed, midnight-ward startAngle, i.e. counter-clockwise, once segments
// retract in that mirrored order).
import { wedgePathD } from "../BrandWheel/brandWheel";

// How long ONE segment's arc takes to sweep from its start angle to its own end
// angle. Sized so the WHOLE sweep-IN (every segment, sequentially) of the
// canonical 6-segment wheel lands at ~1000ms — Gordon's #937 spec (2026-08-22):
// "2 seconds total - 1 second generation, 1 second degeneration". 6 * 168 =
// 1008ms of generation. #937 follow-up (2026-08-22): the loader is now LOCKED to
// a FIXED 6 segments (index.jsx's LOADER_SEGMENT_COUNT — the old 4-7 count
// randomization is removed), so the cycle is a steady 6*168*2 = 2016ms (~2.0s),
// no longer wobbling with the segment count. Was 260ms (a ~1.56s generation)
// before #937.
export const SEGMENT_SWEEP_MS = 168;

// PEAK HOLD (Gordon 2026-08-22): a brief beat holding the FULLY BUILT 6-segment
// wheel — every arc full, no motion — at the TOP of the cycle, between the end
// of generate and the start of degenerate, so a human can register the completed
// wheel BEFORE it starts to degenerate. 300ms: ~100ms is the bare human
// detection threshold, ~300ms reads as an intentional beat and comfortably lets
// the eye register the finished form. This is the hold at PEAK (the full wheel),
// NOT a hold at the blank end. Was 0 (no plateau, #937) before this change.
// `phaseProgress` already returns 1 for EVERY segment across the whole
// [holdStart, holdStart + HOLD_MS) window, so raising this constant is the only
// change the hold needs — the complete static wheel is shown for HOLD_MS, then
// the one-at-a-time reverse (CCW) retract begins. The entry PHASE 1 fade
// (ENTRY_MS, index.jsx) still gives the first whole wheel its own on-screen
// moment before the loop starts.
export const HOLD_MS = 300;

// Segment `index` never starts before segment `index - 1` has finished — a
// strictly sequential stagger (each delay is a whole sweep duration apart), not
// an all-at-once (every delay 0) draw.
export const segmentDelay = (index) => index * SEGMENT_SWEEP_MS;

// The elapsed time (ms into the cycle) at which every segment has finished
// sweeping and the hold begins.
export const holdStartMs = (segmentCount) => segmentCount * SEGMENT_SWEEP_MS;

// How long ONE segment's arc takes to retract (sweep back down to nothing) —
// mirrors SEGMENT_SWEEP_MS so the shrink reads at the same pace as the grow.
export const SEGMENT_RETRACT_MS = SEGMENT_SWEEP_MS;

// Mirrors `segmentDelay` (IN): a WHOLE retract duration apart, so segment N+1
// never starts leaving until segment N has fully gone — one at a time, same
// as the draw-in (ISP-9999 #935: a shorter, overlapping stagger here used to
// let 2 segments shrink concurrently, a cascade rather than a relay). Every
// segment still shrinking gradually (rather than vanishing) is what keeps the
// per-frame drawn-arc total from ever free-falling to blank.
export const RETRACT_STAGGER_MS = SEGMENT_RETRACT_MS;

// Segment `index`'s retract never starts before `retractDelay(index)` ms into
// the OUT phase (elapsed since the hold ended) — strictly sequential AND
// non-overlapping: segment `index` fully finishes before `index + 1` begins.
export const retractDelay = (index) => index * RETRACT_STAGGER_MS;

// DEGENERATE ORDER (ISP-9999 #966, Gordon 2026-08-21): generation draws
// segment 0 (the wedge immediately CLOCKWISE of midnight/12-o'clock, since
// brandWheel.js's `a0 = 0` starting point IS midnight) first, then 1, 2, ...
// on around the ring — sequentially CLOCKWISE FROM MIDNIGHT. Degeneration must
// begin at that same midnight point but unwind COUNTER-CLOCKWISE, so the
// LAST segment (adjacent to midnight from the other side) retracts FIRST and
// segment 0 retracts LAST — the exact reverse of the draw-in order. Mapping a
// position `index` to its retract-order index this way is the only change
// needed: `retractDelay`/`retractProgress` below stay index-generic, and
// `sweepPathD`'s existing a0-anchored shrink (endAngle sweeping back down to
// its own fixed startAngle) is already a counter-clockwise arc-wipe once the
// segments themselves retract in this mirrored order.
export const retractOrderIndex = (segmentCount, index) => segmentCount - 1 - index;

// How long the whole OUT phase takes: the last segment's own delay plus its
// full retract window.
export const retractDurationMs = (segmentCount) =>
  segmentCount <= 0 ? 0 : retractDelay(segmentCount - 1) + SEGMENT_RETRACT_MS;

// One full generation cycle: every segment's sequential sweep-IN (~1s) + the
// PEAK HOLD (300ms holding the complete wheel static, Gordon 2026-08-22) + every
// segment's one-at-a-time sweep-OUT (~1s) = ~2.316s total. Past this the wheel
// clears (already fully retracted, not snapped) and a freshly randomized one
// starts sweeping in.
export const cycleDurationMs = (segmentCount) =>
  holdStartMs(segmentCount) + HOLD_MS + retractDurationMs(segmentCount);

// 0 (not yet started) -> 1 (fully swept) for ONE segment at `elapsedMs` into the
// cycle.
export function sweepProgress(elapsedMs, index) {
  const delay = segmentDelay(index);
  if (elapsedMs <= delay) return 0;
  const t = (elapsedMs - delay) / SEGMENT_SWEEP_MS;
  return t >= 1 ? 1 : t;
}

// The mirror of `sweepProgress`: 1 (still fully drawn) -> 0 (fully retracted)
// for ONE segment at `elapsedOutMs` into the OUT phase (elapsed since the hold
// ended). Reuses `sweepPathD` unchanged below — shrinking is just this
// progress running downhill instead of uphill.
export function retractProgress(elapsedOutMs, index) {
  const delay = retractDelay(index);
  if (elapsedOutMs <= delay) return 1;
  const t = (elapsedOutMs - delay) / SEGMENT_RETRACT_MS;
  return t >= 1 ? 0 : 1 - t;
}

// One segment's progress (0 => nothing drawn, 1 => fully drawn) at `elapsedMs`
// into the WHOLE cycle, whichever of the three phases (sweep-in / hold /
// sweep-out) that instant falls in. The sweep-IN uses `index` directly (CW
// from midnight); the sweep-OUT uses `retractOrderIndex` (CCW from midnight —
// see its comment above) so the two phases unwind in mirrored order.
function phaseProgress(segmentCount, elapsedMs, index) {
  const inEnd = holdStartMs(segmentCount);
  const holdEnd = inEnd + HOLD_MS;
  if (elapsedMs < inEnd) return sweepProgress(elapsedMs, index);
  if (elapsedMs < holdEnd) return 1;
  return retractProgress(elapsedMs - holdEnd, retractOrderIndex(segmentCount, index));
}

// The wedge `d` for ONE segment at a given sweep progress: startAngle fixed at
// `a0Deg`, endAngle interpolated up to `a0Deg + spanDeg` — the arcTween above,
// reproduced with the real Wheel's own geometry function. An undrawn (progress
// 0) segment renders NOTHING (empty `d`), not a zero-opacity full wedge — the
// property that changes frame to frame is the arc itself, never opacity.
export function sweepPathD(a0Deg, spanDeg, progress, opts) {
  if (progress <= 0) return "";
  const currentEnd = a0Deg + spanDeg * Math.min(progress, 1);
  return wedgePathD(a0Deg, currentEnd, opts);
}

// One animation frame for the whole wheel: the `d` for every segment (in their
// fixed clockwise order) plus whether the cycle (every segment swept in + the
// hold + every segment swept back out) has finished.
export function computeFrame(spans, elapsedMs, opts) {
  let a0 = 0;
  const ds = spans.map((span, i) => {
    const d = sweepPathD(a0, span, phaseProgress(spans.length, elapsedMs, i), opts);
    a0 += span;
    return d;
  });
  return { ds, done: elapsedMs >= cycleDurationMs(spans.length) };
}

// The total drawn ARC (in degrees, summed across every segment) at `elapsedMs`
// into the cycle — a pure geometric proxy for "how much of the ring is on
// screen right now", independent of any rendered `d` string. This is the
// mechanical gate for the #908 regression: a hard-cut handoff makes this value
// free-fall from (near) `sum(spans)` to 0 in a single frame; the mirrored
// sweep-out above makes it ramp down gradually instead. See
// arcSweepEngine.test.js's "saturation baseline" suite.
export function frameCoverageDeg(spans, elapsedMs) {
  return spans.reduce((sum, span, i) => sum + span * phaseProgress(spans.length, elapsedMs, i), 0);
}
